import type { Schemas } from '#shopware'
import { useShopwareContext } from '@shopware/composables'
import { productIncludes } from '~/utils/includes/product'

/**
 * Loads products by their product numbers and preserves the order they were provided in.
 * Products that fail to resolve are omitted from the result.
 *
 * @param productNumbers - Array of product numbers (SKUs) in the desired display order
 * @returns Promise resolving to an array of products in the same order, with unresolved products omitted
 */
export async function loadKevelProducts(
    productNumbers: string[]
): Promise<Schemas['Product'][]> {
    if (productNumbers.length === 0) {
        return []
    }

    const { apiClient } = useShopwareContext()

    try {
        const { data } = await apiClient.invoke('readProduct post /product', {
            body: {
                includes: productIncludes,
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'productNumber',
                        value: productNumbers.join('|')
                    }
                ],
                limit: productNumbers.length,
                associations: {
                    media: {
                        associations: {
                            media: {}
                        }
                    },
                    cover: { associations: { media: {} } },
                    options: { associations: { group: {} } },
                    properties: { associations: { group: {} } },
                    seoUrls: {}
                }
            }
        })

        // Reorder products to match the original Kevel order
        const orderedProducts = productNumbers
            .map(productNumber => data.elements?.find(p => p.productNumber === productNumber))
            .filter((p): p is NonNullable<typeof p> => p !== undefined)

        return orderedProducts
    } catch (error) {
        console.error('[kevel] Error loading products:', error)
        return []
    }
}
